import './style.css';
import {
  goBack, toast, setDarkroomMode, onScreenChange,
  isAutoDarkroom, getCurrentScreen, applyDarkLevel,
} from './ui.js';
import { prefs } from './prefs.js';
import { seedDefaultPresetsIfEmpty } from './presets.js';
import { openHome, renderHome } from './home.js';
import { onSaveError, onSaved, retrySave } from './scene.js';

/**
 * Zone Notes — エントリポイント
 * 起動、共通の戻る処理、暗室モードの復元と自動切替、撮影後の処理。
 */

async function bootstrap() {
  // 戻る（[data-back]）
  document.body.addEventListener('click', (e) => {
    const back = e.target.closest('[data-back]');
    if (back) {
      e.preventDefault();
      goBack();
    }
  });

  // 27. 関連する教材の章へ（[data-chapter]）
  document.body.addEventListener('click', (e) => {
    const link = e.target.closest('[data-chapter]');
    if (!link) return;
    e.preventDefault();
    import('./learn.js').then((m) => m.openLearnArticle(link.dataset.chapter));
  });

  applyDarkLevel();
  // 暗室モードの状態復元（手動で切り替えたものだけ記憶している）
  if (prefs.darkroom) setDarkroomMode(true, { fade: false });

  // 現像画面で自動的に暗室にしたときは、離れると元に戻す
  onScreenChange((name, prev) => {
    if (prev === 'development' && name !== 'development' && isAutoDarkroom()) {
      setDarkroomMode(false);
    }
    if (name === 'home') renderHome().catch(() => {});
  });

  bindSaveErrorBar();

  seedDefaultPresetsIfEmpty();

  // 9. 端末の保存領域を「消されにくい」扱いにするよう求める（補助。バックアップの代わりではない）
  try {
    if (navigator.storage?.persist && !(await navigator.storage.persisted())) {
      await navigator.storage.persist();
    }
  } catch (e) { console.info('[storage] persist not available:', e?.message); }

  // Service Worker（本番のみ）
  if ('serviceWorker' in navigator) {
    if (import.meta.env.PROD) {
      try { await navigator.serviceWorker.register('/sw.js'); }
      catch (err) { console.warn('[sw] register failed:', err); }
    } else {
      const regs = await navigator.serviceWorker.getRegistrations();
      regs.forEach((r) => r.unregister());
    }
  }

  await openHome({ anim: 'none' });
}

/**
 * 8. 自動保存に失敗したら、画面の上に消えない帯を出す。保存できたら消す。
 */
function bindSaveErrorBar() {
  onSaveError(({ quota }) => {
    let bar = document.getElementById('save-error');
    if (!bar) {
      bar = document.createElement('div');
      bar.id = 'save-error';
      bar.className = 'save-error';
      bar.setAttribute('role', 'alert');
      document.getElementById('app').appendChild(bar);
    }
    bar.innerHTML = `<span style="flex:1">${quota
      ? '保存できませんでした。端末の空き容量が足りません。不要な写真やシーンを消してから、もう一度保存してください。'
      : '保存できませんでした。少し待って自動でやり直します。'}</span><button type="button" data-retry>もう一度保存</button>`;
    bar.querySelector('[data-retry]').addEventListener('click', async () => {
      try { await retrySave(); toast('保存しました'); } catch { toast('まだ保存できません'); }
    });
  });
  onSaved(() => document.getElementById('save-error')?.remove());
}

bootstrap().catch((err) => {
  console.error('[bootstrap] failed:', err);
  toast('起動に失敗しました');
});

// 開発時の確認用（本番ビルドでは使わない）
if (import.meta.env.DEV) window.__zn = { getCurrentScreen };
